import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getLeads, getStats, getUser, logout } from '@/lib/api';
import { Zap, Search, Users, FileText, Clock, Settings, LogOut, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const STATUSES = ['', 'New Submission', 'Reviewing Photos', 'Needs More Info', 'Quote in Progress', 'Quote Sent', 'Follow-Up', 'Sold', 'Lost'];

const statusColor = (status) => {
  const colors = {
    'New Submission': 'bg-blue-100 text-blue-700 border-blue-200',
    'Reviewing Photos': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'Needs More Info': 'bg-orange-100 text-orange-700 border-orange-200',
    'Quote in Progress': 'bg-purple-100 text-purple-700 border-purple-200',
    'Quote Sent': 'bg-green-100 text-green-700 border-green-200',
    'Follow-Up': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'Sold': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'Lost': 'bg-red-100 text-red-700 border-red-200'
  };
  return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // `search` is intentionally read via a ref (not a direct dependency) so
  // typing in the search box does NOT auto-trigger a reload — search is only
  // applied when the user submits the search form (handleSearch below).
  // This preserves the original "search on submit" UX while still letting
  // loadData satisfy react-hooks/exhaustive-deps correctly.
  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsResp, statsResp] = await Promise.all([
        getLeads({ page, status: statusFilter, search: searchRef.current, limit: 15 }),
        getStats()
      ]);
      setLeads(leadsResp.data.leads);
      setTotalPages(leadsResp.data.pages);
      setStats(statsResp.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [page, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Admin Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[hsl(var(--border))]">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-[var(--brand-ocean-700)]" />
              <span className="font-heading font-bold text-[var(--brand-ocean-700)]">Snap Windows</span>
            </div>
            <Badge variant="outline" className="text-xs border-[var(--brand-ocean-600)] text-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/30">Admin</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')} className="text-[var(--brand-slate-600)] hover:text-[var(--brand-ocean-700)]">
              <Settings className="h-4 w-4" />
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <span className="text-sm text-[var(--brand-slate-600)] hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-red-500 hover:text-red-600 hover:bg-red-50">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Leads', value: stats.total_leads || 0, icon: Users, color: 'text-[var(--brand-ocean-700)]', bg: 'bg-[var(--brand-ocean-100)]' },
            { label: 'New', value: stats.new_leads || 0, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-100' },
            { label: 'In Progress', value: stats.in_progress || 0, icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
            { label: 'Quotes Sent', value: stats.quotes_sent || 0, icon: FileText, color: 'text-green-600', bg: 'bg-green-100' }
          ].map((s) => (
            <Card key={s.label} className="rounded-2xl border-2 hover:shadow-[var(--shadow-soft)] transition-shadow duration-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl ${s.bg} flex items-center justify-center ${s.color}`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-[var(--brand-slate-900)]">{s.value}</p>
                  <p className="text-xs text-[var(--brand-slate-600)]">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--brand-slate-600)]" />
              <Input data-testid="admin-leads-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, phone, or address..." className="pl-9 h-11" />
            </div>
            <Button type="submit" variant="outline" className="rounded-xl h-11 px-5 border-2 font-medium">Search</Button>
          </form>
          <Select value={statusFilter || 'all'} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[200px] h-11 border-2" data-testid="admin-leads-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.filter(Boolean).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Leads Table */}
        <Card className="rounded-2xl overflow-hidden border-2">
          <div className="overflow-x-auto">
            <Table data-testid="admin-leads-table">
              <TableHeader>
                <TableRow className="bg-[hsl(var(--secondary))]">
                  <TableHead className="font-heading font-semibold">Customer</TableHead>
                  <TableHead className="font-heading font-semibold">Contact</TableHead>
                  <TableHead className="font-heading font-semibold">Location</TableHead>
                  <TableHead className="font-heading font-semibold">Project</TableHead>
                  <TableHead className="font-heading font-semibold">Status</TableHead>
                  <TableHead className="font-heading font-semibold">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id} className="cursor-pointer hover:bg-[hsl(var(--secondary))]/50 transition-colors duration-200" onClick={() => navigate(`/admin/leads/${lead.id}`)} data-testid="admin-leads-table-row">
                    <TableCell className="font-medium text-[var(--brand-slate-900)]">{lead.first_name} {lead.last_name}</TableCell>
                    <TableCell>
                      <div className="text-sm text-[var(--brand-slate-900)]">{lead.phone}</div>
                      <div className="text-xs text-[var(--brand-slate-600)]">{lead.email}</div>
                    </TableCell>
                    <TableCell className="text-sm text-[var(--brand-slate-900)]">{lead.city}, FL {lead.zip_code}</TableCell>
                    <TableCell className="text-sm">
                      <div className="text-[var(--brand-slate-900)]">{lead.property_type}</div>
                      <div className="text-xs text-[var(--brand-slate-600)]">{lead.num_windows}W / {lead.num_doors}D</div>
                    </TableCell>
                    <TableCell><Badge className={`${statusColor(lead.status)} font-medium text-xs border`}>{lead.status}</Badge></TableCell>
                    <TableCell className="text-xs text-[var(--brand-slate-600)]">{new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</TableCell>
                  </TableRow>
                ))}
                {leads.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-[var(--brand-slate-600)]">No leads found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-[var(--brand-slate-600)]">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl border-2">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-xl border-2">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
